// src/components/CoffeeNews.jsx
import React, { useEffect, useState } from "react";
import { Calendar, ExternalLink, Coffee, Clock, Share2 } from "lucide-react";

export const CoffeeNews = () => {
  // Placeholder inicial (mostra seu layout até carregar os reais)
  const [newsData, setNewsData] = useState([
    {
      id: 1,
      title:
        "Safra de café arábica 2025 promete recordes históricos no Brasil",
      summary:
        "Produtores relatam expectativas otimistas para a próxima colheita com condições climáticas favoráveis e investimentos em tecnologia.",
      publishedAt: "2025-01-27T08:00:00-03:00",
      source: "AgroNews",
      category: "Produção",
      url: "#",
    },
    {
      id: 2,
      title:
        "Preços do café verde sobem 30% no mercado internacional",
      summary:
        "Demanda crescente por café de qualidade impulsiona valorização dos grãos brasileiros nas bolsas mundiais.",
      publishedAt: "2025-01-26T14:30:00-03:00",
      source: "CaféBrasil",
      category: "Mercado",
      url: "#",
    },
    {
      id: 3,
      title:
        "Blockchain revoluciona rastreabilidade do café brasileiro",
      summary:
        "Novos sistemas garantem origem e qualidade dos grãos desde a fazenda até o consumidor final através de contratos inteligentes.",
      publishedAt: "2025-01-26T10:15:00-03:00",
      source: "TechAgro",
      category: "Tecnologia",
      url: "#",
    },
  ]);

  useEffect(() => {
    // Busca 3 notícias reais do backend
    fetch("https://cofy.app/api/quote.php?op=cofy_news_cafe&n=3", { cache: "no-store" })
      .then((r) => r.json())
      .then((json) => {
        if (json?.ok && Array.isArray(json.items) && json.items.length >= 3) {
          const mapped = json.items.slice(0, 3).map((it, idx) => ({
            id: idx + 1,
            title: it.title || "",
            summary: it.summary || "",
            publishedAt: it.publishedAt || new Date().toISOString(),
            source: it.source || "Notícias Agrícolas",
            category: "Café",
            url: it.url || "#",
          }));
          setNewsData(mapped);
        }
      })
      .catch(() => {
        // se falhar, mantém os placeholders (layout idêntico)
      });
  }, []);

  // ===== compartilhamento WhatsApp -> linka para COFY.APP =====
  const COFY_SHARE_BASE = "https://cofy.app/";
  const buildWhatsAppLink = (item) => {
    const url = `${COFY_SHARE_BASE}?utm_source=whatsapp&utm_medium=share&utm_campaign=coffee_news&nid=${encodeURIComponent(item?.id ?? "")}`;
    const text = `☕ ${item?.title ?? ""}\n${item?.summary ? item.summary + "\n" : ""}Veja mais em: ${url}`;
    return `https://wa.me/?text=${encodeURIComponent(text)}`;
  };

  const formatDate = (dateString) => {
    const d = new Date(dateString);
    return d.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.max(
      0,
      Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
    );
    if (diffInHours < 1) return "Agora há pouco";
    if (diffInHours < 24) return `${diffInHours}h atrás`;
    return `${Math.floor(diffInHours / 24)}d atrás`;
  };

  return (
    <section className="py-20 px-4 bg-background-dark" id="news">
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-2xl border border-white/10">
              <Coffee className="w-8 h-8 text-yellow-400" />
            </div>
          </div>

          <h2 className="text-4xl md:text-5xl font-black text-primary-text mb-6">
            Últimas Notícias do Café
          </h2>

          <p className="text-xl text-muted-text max-w-3xl mx-auto">
            Fique por dentro das principais novidades do mercado cafeeiro
          </p>
        </div>

        {/* Featured news */}
        <div className="mb-12">
          <div className="bg-card-dark/50 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden hover:border-white/20 transition-all duration-300 group">
            <div className="p-8">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-green-400">
                    DESTAQUE
                  </span>
                  <span className="text-sm text-muted-text">•</span>
                  <span className="text-sm text-yellow-400 font-medium">
                    {newsData[0].category}
                  </span>
                </div>
                <div className="flex items-center space-x-2 text-muted-text">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">
                    {getTimeAgo(newsData[0].publishedAt)}
                  </span>
                </div>
              </div>

              <h3 className="text-2xl font-bold text-primary-text mb-4 group-hover:text-green-400 transition-colors">
                {newsData[0].title}
              </h3>

              <p className="text-lg text-muted-text mb-6 leading-relaxed">
                {newsData[0].summary}
              </p>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-muted-text" />
                  <span className="text-sm text-muted-text">
                    {formatDate(newsData[0].publishedAt)}
                  </span>
                  <span className="text-muted-text">•</span>
                  <span className="text-sm text-green-400 font-medium">
                    {newsData[0].source}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  {/* Compartilhar no WhatsApp */}
                  <button
                    className="flex items-center space-x-2 text-green-400 hover:text-primary-text transition-colors group"
                    onClick={() =>
                      window.open(buildWhatsAppLink(newsData[0]), "_blank", "noopener,noreferrer")
                    }
                    title="Compartilhar no WhatsApp"
                  >
                    <span className="text-sm font-medium">Compartilhar</span>
                    <Share2 className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>

                  {/* Ler mais (link externo da notícia, se houver) */}
                  <button
                    className="flex items-center space-x-2 text-green-400 hover:text-primary-text transition-colors group"
                    onClick={() =>
                      newsData[0].url && window.open(newsData[0].url, "_blank", "noopener,noreferrer")
                    }
                  >
                    <span className="text-sm font-medium">Ler mais</span>
                    <ExternalLink className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Other news grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {newsData.slice(1).map((news, index) => (
            <div
              key={news.id}
              className="bg-card-dark/50 backdrop-blur-sm rounded-2xl border border-white/10 p-6 hover:border-white/20 transition-all duration-300 group hover:scale-105"
              style={{ animationDelay: `${index * 200}ms` }}
            >
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                <span className="text-xs text-blue-400 font-medium">
                  {news.source}
                </span>
                <span className="text-muted-text">•</span>
                <span className="text-xs text-yellow-400">{news.category}</span>
                <span className="text-muted-text">•</span>
                <span className="text-xs text-muted-text">
                  {getTimeAgo(news.publishedAt)}
                </span>
              </div>

              <h4 className="text-lg font-bold text-primary-text mb-3 group-hover:text-blue-400 transition-colors line-clamp-2">
                {news.title}
              </h4>

              <p className="text-sm text-muted-text mb-4 line-clamp-3">
                {news.summary}
              </p>

              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-text">
                  {formatDate(news.publishedAt)}
                </span>

                <div className="flex items-center gap-2">
                  {/* Compartilhar no WhatsApp */}
                  <button
                    className="text-green-400 hover:text-primary-text transition-colors"
                    onClick={() =>
                      window.open(buildWhatsAppLink(news), "_blank", "noopener,noreferrer")
                    }
                    title="Compartilhar no WhatsApp"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>

                  {/* Link externo original (se quiser manter) */}
                  <button
                    className="text-blue-400 hover:text-primary-text transition-colors"
                    onClick={() => news.url && window.open(news.url, "_blank", "noopener,noreferrer")}
                    title="Abrir fonte"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View all */}
        <div className="text-center">
          <button
            className="btn-secondary"
            onClick={() =>
              window.open(
                "https://www.noticiasagricolas.com.br/noticias/cafe/",
                "_blank"
              )
            }
          >
            Ver Todas as Notícias
            <ExternalLink className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
};
